import axios from 'axios';

const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = /\/api\/?$/i.test(rawApiUrl) ? rawApiUrl : `${rawApiUrl.replace(/\/+$/, '')}/api`;
// Derive base URL for static files served at '/uploads' (non-API path)
// If base URL ends with '/api', remove it to get the origin for files
const FILE_BASE_URL = API_BASE_URL.replace(/\/?api\/?$/i, '');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,  // Send & receive cookies on every request
});

// ─── Cookie Helpers ──────────────────────────────────────────────

/** Read a cookie by name from document.cookie */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// ─── Request Interceptor ────────────────────────────────────────

apiClient.interceptors.request.use((config) => {
  // 1. Attach CSRF token (read from eq_csrf cookie set by server)
  const csrf = getCookie('eq_csrf');
  if (csrf) {
    config.headers['X-CSRF-Token'] = csrf;
  }

  // 2. Attach Bearer token as fallback (for cross-origin when cookies can't be sent)
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and plan limit errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      [
        'token',
        'user',
        'userId',
        'platformRole',
        'currentWorkspaceId',
        'sa_original_token',
        'sa_original_user',
        'workspaceToken',
        'workspaceUser',
      ].forEach((key) => localStorage.removeItem(key));
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      localStorage.setItem('elvoaq_logout_event', Date.now().toString());
      window.dispatchEvent(new CustomEvent('elvoaq:logout'));
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    // Detect PLAN_LIMIT_REACHED and dispatch a global event for the UI to catch
    if (error?.response?.status === 403 && error?.response?.data?.code === 'PLAN_LIMIT_REACHED') {
      const detail = {
        error: error.response.data.error || 'Plan limit reached',
        limitType: error.response.data.limitType || 'generic',
        current: error.response.data.current,
        max: error.response.data.limit || error.response.data.max,
      };
      window.dispatchEvent(new CustomEvent('plan-limit-reached', { detail }));
    }
    return Promise.reject(error);
  }
);

// Export base API client
export const api = apiClient;

export const publicAPI = {
  sendContact: (data: { name: string; email: string; subject: string; message: string; turnstileToken?: string }) =>
    apiClient.post('/public/contact', data),
};

// Helper to resolve file URLs (handles absolute and relative '/uploads/...')
export const resolveFileUrl = (path: string | undefined | null): string | null => {
  if (!path) return null;
  // If already absolute (http/https), return as-is
  if (/^https?:\/\//i.test(path)) return path;
  // Ensure leading slash
  const normalized = path.startsWith('/') ? path : `/${path}`;
  // Use FILE_BASE_URL; fallback to deriving from API_BASE_URL
  const base = FILE_BASE_URL || API_BASE_URL.replace(/\/api\/?$/i, '');
  return `${base}${normalized}`;
};

export const fileBaseUrl = FILE_BASE_URL;

// Auth endpoints
export const authAPI = {
  signup: (email: string, password: string, firstName: string, lastName?: string, turnstileToken?: string) =>
    apiClient.post('/auth/signup', { email, password, firstName, lastName, turnstileToken }),
  login: (email: string, password: string, turnstileToken?: string) =>
    apiClient.post('/auth/login', { email, password, turnstileToken }),
  loginWithGoogle: (idToken: string) =>
    apiClient.post('/auth/google', { idToken }),
  loginWithApple: (identityToken: string, firstName?: string, lastName?: string) =>
    apiClient.post('/auth/apple', { identityToken, firstName, lastName }),
  logout: () => apiClient.post('/auth/logout'),
  verifyEmail: (token: string) =>
    apiClient.post('/auth/verify-email', { token }),
  resendVerification: (email: string) =>
    apiClient.post('/auth/resend-verification', { email }),
  forgotPassword: (email: string) =>
    apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    apiClient.post('/auth/reset-password', { token, password }),
  refresh: () => apiClient.post('/auth/refresh'),
};

// Item endpoints
export const itemsAPI = {
  getAll: (filters?: any) => apiClient.get('/items', { params: filters }),
  getById: (id: string) => apiClient.get(`/items/${id}`),
  create: (data: any) => apiClient.post('/items', data),
  update: (id: string, data: any) => apiClient.put(`/items/${id}`, data),
  delete: (id: string) => apiClient.delete(`/items/${id}`),
  restore: (id: string) => apiClient.post(`/items/${id}/restore`),
  deletePermanent: (id: string) => apiClient.delete(`/items/${id}/permanent`),
  toggleFavorite: (id: string) => apiClient.patch(`/items/${id}/favorite`),
  search: (query: string) => apiClient.get('/items/search', { params: { q: query } }),
};

// Calendar endpoints
export const calendarAPI = {
  getEvents: (params?: { from?: string; to?: string }) => apiClient.get('/calendar/events', { params }),
  createEvent: (data: any) => apiClient.post('/calendar/events', data),
  updateEvent: (id: string, data: any) => apiClient.put(`/calendar/events/${id}`, data),
  moveEvent: (id: string, date: string) => apiClient.patch(`/calendar/events/${id}/move`, { date }),
  deleteEvent: (id: string) => apiClient.delete(`/calendar/events/${id}`),
  getTrashedEvents: () => apiClient.get('/calendar/events/trash'),
  restoreEvent: (id: string) => apiClient.post(`/calendar/events/${id}/restore`),
  permanentDeleteEvent: (id: string) => apiClient.delete(`/calendar/events/${id}/permanent`),
};

// Collections endpoints
export const collectionsAPI = {
  getAll: () => apiClient.get('/collections'),
  create: (data: any) => apiClient.post('/collections', data),
  update: (id: string, data: any) => apiClient.put(`/collections/${id}`, data),
  delete: (id: string) => apiClient.delete(`/collections/${id}`),
  addItem: (collectionId: string, data: any) => apiClient.post(`/collections/${collectionId}/items`, data),
  removeItem: (collectionId: string, itemId: string) => apiClient.delete(`/collections/${collectionId}/items/${itemId}`),
};

// Category endpoints
export const categoriesAPI = {
  getAll: () => apiClient.get('/categories'),
  getById: (id: string) => apiClient.get(`/categories/${id}`),
  create: (data: any) => apiClient.post('/categories', data),
  update: (id: string, data: any) => apiClient.put(`/categories/${id}`, data),
  delete: (id: string) => apiClient.delete(`/categories/${id}`),
};

// Metadata endpoints
export const metadataAPI = {
  getCurrencies: () => apiClient.get('/metadata/currencies'),
};

// AI endpoints
export const aiAPI = {
  getInsights: () => apiClient.get('/ai/insights'),
  getSummary: () => apiClient.get('/ai/summary'),
  getReport: () => apiClient.get('/ai/report'),
  generateReminders: () => apiClient.post('/ai/generate-reminders'),
  categorize: (itemId: string) => apiClient.post(`/ai/categorize`, { itemId }),
  suggestCategories: (name: string, description?: string) => 
    apiClient.post('/ai/suggest-categories', { name, description }),
  detectDuplicates: (itemId: string) => apiClient.post(`/ai/detect-duplicates`, { itemId }),
  predictValue: (itemId: string) => apiClient.post(`/ai/predict-value`, { itemId }),
  search: (query: string) => apiClient.get(`/ai/search`, { params: { q: query } }),
};

// AI Chat endpoints
export const aiChatAPI = {
  getChats: () => apiClient.get('/ai-chat/chats'),
  createChat: (title?: string) => apiClient.post('/ai-chat/chats', { title }),
  getChat: (chatId: string) => apiClient.get(`/ai-chat/chats/${chatId}`),
  deleteChat: (chatId: string) => apiClient.delete(`/ai-chat/chats/${chatId}`),
  renameChat: (chatId: string, title: string) => apiClient.patch(`/ai-chat/chats/${chatId}`, { title }),
  sendMessage: (chatId: string, content: string, images?: File[]) => {
    const formData = new FormData();
    formData.append('content', content);
    if (images) {
      images.forEach(img => formData.append('images', img));
    }
    return apiClient.post(`/ai-chat/chats/${chatId}/messages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  analyzeImage: (image: File, prompt?: string) => {
    const formData = new FormData();
    formData.append('image', image);
    if (prompt) formData.append('prompt', prompt);
    return apiClient.post('/ai-chat/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Duplicate Finder endpoints
export const duplicatesAPI = {
  scan: (image: File) => {
    const formData = new FormData();
    formData.append('image', image);
    return apiClient.post('/duplicates/scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getHistory: () => apiClient.get('/duplicates/history'),
  getScan: (scanId: string) => apiClient.get(`/duplicates/scans/${scanId}`),
};

// Item Lock endpoints
export const itemLockAPI = {
  lock: (itemId: string, password: string) =>
    apiClient.post(`/items/${itemId}/lock`, { password }),
  unlock: (itemId: string, password: string) =>
    apiClient.post(`/items/${itemId}/unlock`, { password }),
  verifyLock: (itemId: string, password: string) =>
    apiClient.post(`/items/${itemId}/verify-lock`, { password }),
  changeLockPassword: (itemId: string, currentPassword: string, newPassword: string) =>
    apiClient.post(`/items/${itemId}/change-lock-password`, { currentPassword, newPassword }),
  removeLock: (itemId: string) =>
    apiClient.post(`/items/${itemId}/remove-lock`),
};

// Reminder endpoints
export const remindersAPI = {
  getAll: (params?: { itemId?: string; workspaceId?: string }) => apiClient.get('/reminders', { params }),
  create: (data: { title: string; description?: string; type?: string; priority?: string; dueDate?: string; itemId?: string; assigneeId?: string; workspaceId?: string }) =>
    apiClient.post('/reminders', data),
  update: (id: string, data: any) => apiClient.put(`/reminders/${id}`, data),
  toggle: (id: string) => apiClient.patch(`/reminders/${id}/toggle`),
  delete: (id: string) => apiClient.delete(`/reminders/${id}`),
};

// Note endpoints
export const notesAPI = {
  getAll: (params?: { itemId?: string; workspaceId?: string }) => apiClient.get('/notes', { params }),
  create: (data: { content: string; tags?: string[]; itemId?: string; assigneeId?: string; workspaceId?: string }) =>
    apiClient.post('/notes', data),
  update: (id: string, data: any) => apiClient.put(`/notes/${id}`, data),
  togglePin: (id: string) => apiClient.patch(`/notes/${id}/pin`),
  delete: (id: string) => apiClient.delete(`/notes/${id}`),
};

// Billing endpoints
export const billingAPI = {
  createCheckout: (data: { planId: string; interval: 'month' | 'year'; workspaceId: string; successUrl: string; cancelUrl: string }) =>
    apiClient.post('/billing/checkout', data),
  openPortal: (returnUrl: string) => apiClient.post('/billing/portal', { returnUrl }),
  getWorkspaceSubscription: (workspaceId: string) => apiClient.get(`/billing/workspace/${workspaceId}`),
  getInvoices: () => apiClient.get('/billing/invoices'),
};

// PayPal endpoints
export const paypalAPI = {
  createOrder: (data: { workspaceId: string; planId: string; interval: 'monthly' | 'yearly'; returnUrl: string; cancelUrl: string }) =>
    apiClient.post('/paypal/create-order', data),
  captureOrder: (orderId: string) => apiClient.post('/paypal/capture-order', { orderId }),
  getTransactions: () => apiClient.get('/paypal/transactions'),
};

// Mail settings endpoints
export const mailSettingsAPI = {
  getWorkspace: (workspaceId: string) => apiClient.get(`/mail-settings/workspace/${workspaceId}`),
  updateWorkspace: (workspaceId: string, data: { fromName?: string; fromEmail?: string; replyTo?: string; logoUrl?: string; footerText?: string }) =>
    apiClient.put(`/mail-settings/workspace/${workspaceId}`, data),
  sendTest: (workspaceId: string) => apiClient.post(`/mail-settings/workspace/${workspaceId}/test`),
};

// Notification endpoints
export const notificationsAPI = {
  getAll: (unreadOnly?: boolean) => apiClient.get('/notifications', { params: { unreadOnly } }),
  getTrash: () => apiClient.get('/notifications/trash'),
  markAsRead: (id: string) => apiClient.patch(`/notifications/${id}/read`),
  markAsUnread: (id: string) => apiClient.patch(`/notifications/${id}/unread`),
  markAllAsRead: () => apiClient.patch('/notifications/mark-all-read'),
  delete: (id: string) => apiClient.delete(`/notifications/${id}`),
  deleteAll: () => apiClient.delete('/notifications'),
  restore: (id: string) => apiClient.patch(`/notifications/${id}/restore`),
};

// Notification Settings endpoints
export const notificationSettingsAPI = {
  get: () => apiClient.get('/notification-settings'),
  update: (data: any) => apiClient.put('/notification-settings', data),
  toggle: (type: string, enabled: boolean) => apiClient.patch(`/notification-settings/toggle/${type}`, { enabled }),
  updateQuietHours: (data: { enabled: boolean; start?: string; end?: string }) =>
    apiClient.patch('/notification-settings/quiet-hours', data),
  updateDigest: (data: { daily?: boolean; weekly?: boolean; time?: string }) =>
    apiClient.patch('/notification-settings/digest', data),
  reset: () => apiClient.post('/notification-settings/reset'),
};

// Workspaces endpoints
export const workspacesAPI = {
  getAll: () => apiClient.get('/workspaces'),
  getById: (id: string) => apiClient.get(`/workspaces/${id}`),
  create: (data: { name: string; description?: string; type?: string }) =>
    apiClient.post('/workspaces', data),
  update: (id: string, data: { name?: string; description?: string }) =>
    apiClient.put(`/workspaces/${id}`, data),
  delete: (id: string) => apiClient.delete(`/workspaces/${id}`),
  // Workspace members
  getMembers: (workspaceId: string) => apiClient.get(`/workspaces/${workspaceId}/members`),
  inviteMember: (workspaceId: string, email: string, role: string) =>
    apiClient.post(`/workspaces/${workspaceId}/members/invite`, { email, role }),
  updateMemberRole: (workspaceId: string, memberId: string, role: string) =>
    apiClient.put(`/workspaces/${workspaceId}/members/${memberId}`, { role }),
  removeMember: (workspaceId: string, memberId: string) =>
    apiClient.delete(`/workspaces/${workspaceId}/members/${memberId}`),
};

// Teams endpoints
export const teamsAPI = {
  getByWorkspace: (workspaceId: string) => apiClient.get(`/teams/workspace/${workspaceId}`),
  getAll: (workspaceId: string) => apiClient.get(`/teams/workspace/${workspaceId}`),
  getById: (teamId: string) => apiClient.get(`/teams/${teamId}`),
  create: (data: { workspaceId: string; name: string; description?: string; icon?: string; color?: string }) =>
    apiClient.post('/teams', data),
  update: (teamId: string, data: { name?: string; description?: string; icon?: string; color?: string }) =>
    apiClient.put(`/teams/${teamId}`, data),
  delete: (teamId: string) => apiClient.delete(`/teams/${teamId}`),
  // Team members
  getMembers: (teamId: string) => apiClient.get(`/teams/${teamId}/members`),
  addMember: (teamId: string, data: { email: string; role: string } | string, role?: string) => {
    // Support both old signature (teamId, userId, role) and new signature (teamId, {email, role})
    if (typeof data === 'string') {
      return apiClient.post(`/teams/${teamId}/members`, { memberUserId: data, role });
    }
    return apiClient.post(`/teams/${teamId}/members/invite`, data);
  },
  updateMemberRole: (teamId: string, memberId: string, role: string) =>
    apiClient.put(`/teams/${teamId}/members/${memberId}`, { role }),
  removeMember: (teamId: string, memberId: string) =>
    apiClient.delete(`/teams/${teamId}/members/${memberId}`),
  // Team items
  getItems: (teamId: string) => apiClient.get(`/teams/${teamId}/items`),
  addItem: (teamId: string, itemId: string) => apiClient.post(`/teams/${teamId}/items`, { itemId }),
  removeItem: (teamId: string, itemId: string) => apiClient.delete(`/teams/${teamId}/items/${itemId}`),
};

// User/Settings endpoints
export const userAPI = {
  getProfile: () => apiClient.get('/user/profile'),
  updateProfile: (data: { firstName?: string; lastName?: string; bio?: string }) => 
    apiClient.put('/user/profile', data),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiClient.put('/user/password', { currentPassword, newPassword }),
  changeEmail: (newEmail: string, password: string) =>
    apiClient.put('/user/email', { newEmail, password }),
  toggle2FA: (enabled: boolean) => apiClient.put('/user/2fa', { enabled }),
  deleteAccount: (password: string) => apiClient.delete('/user/account', { data: { password } }),
};

// Audit endpoints
export const auditAPI = {
  getLogs: (filters?: {
    userId?: string;
    action?: string;
    resourceType?: string;
    resourceId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => apiClient.get('/audit', { params: filters }),
  getItemHistory: (itemId: string) => apiClient.get(`/audit/item/${itemId}`),
  getUserActivity: (userId: string) => apiClient.get(`/audit/user/${userId}`),
  getMyActivity: () => apiClient.get('/audit/my-activity'),
  getStats: () => apiClient.get('/audit/stats'),
  getRecent: (limit?: number) => apiClient.get('/audit/recent', { params: { limit } }),
  getActions: () => apiClient.get('/audit/actions'),
  export: (filters?: any) => apiClient.get('/audit/export', { 
    params: filters,
    responseType: 'blob' 
  }),
};

// Currency endpoints
export const currencyAPI = {
  getSupportedCurrencies: () => apiClient.get('/currency/supported'),
  getRates: (baseCurrency?: string) => apiClient.get('/currency/rates', { params: { base: baseCurrency } }),
  convert: (amount: number, from: string, to: string) => 
    apiClient.post('/currency/convert', { amount, from, to }),
  getUserDefault: () => apiClient.get('/currency/user-default'),
  updateUserDefault: (currency: string) => apiClient.patch('/currency/user-default', { currency }),
};

// Super Admin endpoints
export const superAdminAPI = {
  getUsers: (params?: { search?: string; page?: number; limit?: number; role?: string; verified?: string }) =>
    apiClient.get('/super-admin/users', { params }),
  getUserDetail: (id: string) => apiClient.get(`/super-admin/users/${id}`),
  impersonateUser: (id: string) => apiClient.post(`/super-admin/impersonate/${id}`),
  updateUserRole: (id: string, role: string) => apiClient.put(`/super-admin/users/${id}/role`, { role }),
  verifyUserEmail: (id: string) => apiClient.put(`/super-admin/users/${id}/verify`),
  deleteUser: (id: string) => apiClient.delete(`/super-admin/users/${id}`),
  getStats: () => apiClient.get('/super-admin/stats'),
};

// AI Settings endpoints
export const aiSettingsAPI = {
  get: () => apiClient.get('/ai-settings'),
  update: (data: { provider: string; apiKey?: string; model?: string }) =>
    apiClient.put('/ai-settings', data),
  test: () => apiClient.post('/ai-settings/test'),
};

// Hidden Folders endpoints
export const hiddenFoldersAPI = {
  list: () => apiClient.get('/hidden-folders'),
  create: (data: { name: string; description?: string; password: string; icon?: string; color?: string }) =>
    apiClient.post('/hidden-folders', data),
  access: (folderId: string, password: string) =>
    apiClient.post(`/hidden-folders/${folderId}/access`, { password }),
  addItem: (folderId: string, itemId: string, password: string) =>
    apiClient.post(`/hidden-folders/${folderId}/items`, { itemId, password }),
  removeItem: (folderId: string, itemId: string) =>
    apiClient.delete(`/hidden-folders/${folderId}/items/${itemId}`),
  update: (folderId: string, data: { name?: string; description?: string; icon?: string; color?: string; password?: string; newPassword?: string }) =>
    apiClient.put(`/hidden-folders/${folderId}`, data),
  delete: (folderId: string, password: string) =>
    apiClient.delete(`/hidden-folders/${folderId}`, { data: { password } }),
};

// Data Transfer (Import/Export) endpoints
export const dataTransferAPI = {
  getExportFields: () => apiClient.get('/data/export-fields'),
  exportData: (data: { format: 'csv' | 'json'; fields?: string[]; categoryId?: string; startDate?: string; endDate?: string }) =>
    apiClient.post('/data/export', data, { responseType: 'blob' }),
  importData: (data: { items: any[]; mode: 'add' | 'overwrite'; format: string }) =>
    apiClient.post('/data/import', data),
};

// Plan Usage & Limits endpoints
export const planUsageAPI = {
  getUsage: () => apiClient.get('/plan-usage'),
};

// Free Trial endpoints
export const trialAPI = {
  getStatus: () => apiClient.get('/trial/status'),
  start: (planId: string) => apiClient.post('/trial/start', { planId }),
  checkExpiry: () => apiClient.post('/trial/check-expiry'),
  cancel: () => apiClient.post('/trial/cancel'),
};

// Cookie / Consent / Preferences endpoints
export const cookiesAPI = {
  getConsent: () => apiClient.get('/cookies/consent'),
  saveConsent: (consent: { analytics: boolean; preferences: boolean; marketing: boolean }) =>
    apiClient.post('/cookies/consent', consent),
  getPreferences: () => apiClient.get('/cookies/preferences'),
  setTheme: (theme: 'dark' | 'light' | 'system') =>
    apiClient.post('/cookies/preferences/theme', { theme }),
  setLanguage: (language: string) =>
    apiClient.post('/cookies/preferences/language', { language }),
};

export default apiClient;
